import React, { useEffect, useRef, useState, useMemo } from 'react';
import {
  CButton,
  CTable,
  CTableHead,
  CTableHeaderCell,
  CTableRow,
  CTableBody,
  CTableDataCell,
  CCard,
  CCardBody
} from '@coreui/react';
import icone from '../../assets/images/info.png';
import { BsHeartPulse } from 'react-icons/bs';
import './appointment.css';
import { useNavigate } from "react-router-dom";
import api from '../../service/caller';
import { ToastContainer, toast } from 'react-toastify';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';

const ITEMS_PER_PAGE = 10;

const AppointmentHistory = () => {
  const navigate = useNavigate();
  const [UserData, setUserData] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState('');
  const [selectedYear, setSelectedYear] = useState('');
  const [selectedDate, setSelectedDate] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  // Effet pour récupérer les données utilisateur
  useEffect(() => {
    const fetchLoggedUserData = async () => {
      try {
        const response = await api.get('api/usersprofile');
        setUserData(response.data);
      } catch (error) {
        toast.error("Échec de la récupération des données");
      }
    };
    fetchLoggedUserData();
  }, []);

  // Effet pour récupérer les départements
  useEffect(() => {
    const fetchDepartments = async () => {
      try {
        const response = await api.get(`/api/departments`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
          }
        });
        setDepartments(response.data.data);
      } catch (error) {
        console.error('Erreur lors de la récupération des départements:', error);
      }
    };
    fetchDepartments();
  }, []);

  // Effet pour récupérer les rendez-vous
  useEffect(() => {
    const fetchAppointments = async () => {
      try {
        const token = localStorage.getItem('access_token');
        if (!token) throw new Error('Token non trouvé');
        const hospitalIds = Array.isArray(UserData.hospital_id) 
          ? UserData.hospital_id 
          : [UserData.hospital_id];
        const promises = hospitalIds.map(hospitalId =>
          api.get(`/clinic/getAppointmentlist?hospitalId=${hospitalId._id || hospitalId}`, {
            headers: { Authorization: `Bearer ${token}` }
          })
        );
        const responses = await Promise.all(promises);
        const allAppointments = responses.flatMap(response => response.data);
        
        // Filtrage initial
        const validAppointments = allAppointments.filter(appointment =>
          appointment.paymentStatus === true &&
          appointment.doctorId === UserData._id &&
          ['Cancelled', 'Completed'].includes(appointment.status)
        );

        // Vérification des signes vitaux
        const appointmentsWithSignVitalStatus = await Promise.all(
          validAppointments.map(async (appointment) => {
            const signVitalExists = await fetchSignVitalStatus(appointment._id);
            return {
              ...appointment,
              signVitalExists,
            };
          })
        );
        setAppointments(appointmentsWithSignVitalStatus);
      } catch (error) {
        console.error('Erreur lors de la récupération des rendez-vous:', error);
        toast.error("Échec de la récupération des rendez-vous");
      }
    };
    if (UserData && UserData.hospital_id) {
      fetchAppointments();
    }
  }, [UserData]);

  // Vérification des signes vitaux
  const fetchSignVitalStatus = async (appointmentId) => {
    try {
      const response = await api.get(`/clinic/signvitalforappointment/${appointmentId}`);
      return response.data.signVitaldata;
    } catch (error) {
      return false;
    }
  };

  // Filtrage des rendez-vous (avec recherche, date, mois/année)
  const filteredAppointments = useMemo(() => {
    return appointments.filter(appointment => {
      // Filtre de recherche
      const matchesSearch = !searchTerm || 
        (appointment.patientId?.firstname?.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (appointment.patientId?.lastname?.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (appointment.patientId?.phone?.includes(searchTerm));
      
      // Filtre de date
      const appointmentDateStr = appointment.appointmentDate?.split('T')[0];
      if (!appointmentDateStr) return matchesSearch;

      if (selectedDate) {
        return appointmentDateStr === selectedDate && matchesSearch;
      }

      if (selectedMonth && selectedYear) {
        const monthStr = selectedMonth.padStart(2, '0');
        return appointmentDateStr.startsWith(`${selectedYear}-${monthStr}`) && matchesSearch;
      }

      return matchesSearch;
    });
  }, [appointments, searchTerm, selectedMonth, selectedYear, selectedDate]);

  // Pagination
  const totalPages = Math.ceil(filteredAppointments.length / ITEMS_PER_PAGE);
  const currentAppointments = useMemo(() => {
    return filteredAppointments.slice(
      (currentPage - 1) * ITEMS_PER_PAGE,
      currentPage * ITEMS_PER_PAGE
    );
  }, [filteredAppointments, currentPage]);

  // Gestion des changements de filtre
  const handleFilterChange = (setter) => (e) => {
    setter(e.target.value);
    setCurrentPage(1);
  };

  // Navigation
  const handleGoBack = () => navigate(-1);
  const handleViewHistory2 = (id) => navigate(`/hospitaladmin/patientfilemedical/${id}`);
  const handleAddOrEditSignsVital = (appointmentId) => {
    navigate(`/appointment/${appointmentId}/signvital`, {
      state: { existingSignsVital: true }
    });
  };

  return (
    <div className="py-4" style={{ fontFamily: 'Arial, sans-serif' }}>
      <h2 className="fw-bold" style={{ color: '#0056B3', fontSize: '42px', marginBottom: '40px' }}>
        Vos rendez-vous
      </h2>
      <div className="card p-4">
        <div className="card-body">
          {/* Barre de navigation */}
          <nav className="navbar navbar-expand-lg navbar-light bg-white mb-4">
            <div className="d-flex align-items-center gap-3 w-100">
              <button className="btn btn-outline-primary d-flex align-items-center" onClick={handleGoBack}>
                <i className="bi bi-arrow-left me-2"></i> Retour
              </button>
              <div className="input-group" style={{ maxWidth: '200px' }}>
                <span className="input-group-text bg-white border-end-0">
                  <i className="bi bi-search"></i>
                </span>
                <input 
                  type="text" 
                  className="form-control border-start-0" 
                  placeholder="Rechercher..." 
                  value={searchTerm}
                  onChange={handleFilterChange(setSearchTerm)}
                />
              </div>
              <div className="input-group" style={{ maxWidth: '200px' }}>
                <input 
                  type="date" 
                  className="form-control" 
                  value={selectedDate}
                  onChange={handleFilterChange(setSelectedDate)}
                />
              </div>
            </div>
          </nav>

          {/* Tableau des rendez-vous */}
          <CTable className="mt-5 table ctable-no-border" align="middle" responsive>
            <CTableHead className="thead-light">
              <CTableRow className='tablehead text-primary'>
                <CTableHeaderCell className='text-primary' scope="col">Heure</CTableHeaderCell>
                <CTableHeaderCell className='text-primary' scope="col">Date</CTableHeaderCell>
                <CTableHeaderCell className='text-primary' scope="col">Patient</CTableHeaderCell>
                <CTableHeaderCell className='text-primary' scope="col">Téléphone</CTableHeaderCell>
                <CTableHeaderCell className='text-primary' scope="col">Actions</CTableHeaderCell>
                <CTableHeaderCell className='text-primary' scope="col">Statut</CTableHeaderCell>
              </CTableRow>
            </CTableHead>
            <CTableBody>
              {currentAppointments.length > 0 ? (
                currentAppointments.map((appointment, index) => (
                  <CTableRow className="ctable-row" key={appointment._id || index}>
                    <CTableDataCell className='text-primary'>
                      <p style={{ lineHeight: '5px', marginTop: '15px' }}>{appointment.appointmentTime}</p>
                      <p style={{ lineHeight: '5px', width: '40px', backgroundColor: 'blue', height: '1px' }}></p>
                      <p style={{ lineHeight: '5px' }}>{appointment.appointmentEndTime}</p>
                    </CTableDataCell>
                    <CTableDataCell>{appointment.appointmentDate?.split("T")[0]}</CTableDataCell>
                    <CTableDataCell>
                      {appointment.patientId?.firstname || 'Inconnu'} {appointment.patientId?.lastname || ''}
                    </CTableDataCell>
                    <CTableDataCell>{appointment.patientId?.phone || '-'}</CTableDataCell>
                    <CTableDataCell>
                      <OverlayTrigger
                        placement="top"
                        overlay={<Tooltip>
                          {appointment.signVitalExists ? 'Modifier les signes vitaux' : 'Ajouter des signes vitaux'}
                        </Tooltip>}
                      >
                        <span
                          onClick={() => handleAddOrEditSignsVital(appointment._id)}
                          style={{
                            color: appointment.signVitalExists ? '#198754' : '#0d6efd',
                            fontSize: '1.5rem',
                            cursor: 'pointer'
                          }}
                        >
                          <BsHeartPulse />
                        </span>
                      </OverlayTrigger>
                      <img
                        className="me-2 ms-2 btn cursor-pointer"
                        onClick={() => handleViewHistory2(appointment?.patientId?._id)}
                        src={icone}
                        alt="Fiche médicale"
                      />
                    </CTableDataCell>
                    <CTableDataCell className='Patientlist'>
                      <span className={
                        appointment.status === 'Completed' ? 'coloredsucess' : 'coloredechec'
                      }>
                        {appointment.status}
                      </span>
                    </CTableDataCell>
                  </CTableRow>
                ))
              ) : (
                <CTableRow>
                  <CTableDataCell colSpan="6" className="text-center">
                    Aucun rendez-vous trouvé
                  </CTableDataCell>
                </CTableRow>
              )}
            </CTableBody>
          </CTable>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="d-flex justify-content-between align-items-center mt-4">
              <div>
                Affichage de {(currentPage - 1) * ITEMS_PER_PAGE + 1} à{' '}
                {Math.min(currentPage * ITEMS_PER_PAGE, filteredAppointments.length)} sur{' '}
                {filteredAppointments.length} résultats
              </div>
              <div>
                <button 
                  className="btn btn-outline-secondary me-2" 
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                >
                  Précédent
                </button>
                <span className="me-2">
                  Page {currentPage} sur {totalPages || 1}
                </span>
                <button 
                  className="btn btn-outline-secondary" 
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages || totalPages === 0}
                >
                  Suivant
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
      <ToastContainer />
    </div>
  );
};

export default AppointmentHistory;